import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { getTalents, getTalentDetail, createTalent, updateTalent, deleteTalent, updateTalentStatus, addTag, deleteTag, addCommunication, checkDuplicateTalent } from '../../services/talentService';
import type { Resume, TalentDetail } from '../../services/talentService';
import AddTalentModal from './components/AddTalentModal';

export default function TalentsPage() {
  const [talents, setTalents] = useState<Resume[]>([]);
  const [selectedTalent, setSelectedTalent] = useState<TalentDetail | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('list');
  const [showDuplicateCheck, setShowDuplicateCheck] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // 加载人才列表
  useEffect(() => {
    loadTalents();
  }, [filter, searchQuery]);

  const loadTalents = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (filter !== 'all') {
        filters.status = filter;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }
      const data = await getTalents(filters);
      setTalents(data);
    } catch (error) {
      console.error('加载人才列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 查看人才详情
  const handleViewDetail = async (id: string) => {
    try {
      const detail = await getTalentDetail(id);
      setSelectedTalent(detail);
    } catch (error) {
      console.error('加载人才详情失败:', error);
    }
  };

  // 创建人才
  const handleCreateTalent = async () => {
    try {
      // 检查重复
      const duplicates = await checkDuplicateTalent(newTalent.phone, newTalent.email);
      if (duplicates.length > 0) {
        alert('发现重复人才记录，请检查手机号或邮箱');
        return;
      }

      await createTalent({
        ...newTalent,
        skills: []
      });
      setShowCreateModal(false);
      setNewTalent({
        name: '',
        position: '',
        phone: '',
        email: '',
        expected_salary: '',
        location: '',
        years_of_experience: 0
      });
      loadTalents();
    } catch (error) {
      console.error('创建人才失败:', error);
      alert('创建失败，请重试');
    }
  };

  // 更新状态
  const handleUpdateStatus = async (resumeId: string, status: string) => {
    try {
      await updateTalentStatus(resumeId, status);
      loadTalents();
      if (selectedTalent?.id === resumeId) {
        const detail = await getTalentDetail(resumeId);
        setSelectedTalent(detail);
      }
    } catch (error) {
      console.error('更新状态失败:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '在职-考虑机会': return 'bg-yellow-100 text-yellow-800';
      case '离职-立即到岗': return 'bg-green-100 text-green-800';
      case '离职-考虑机会': return 'bg-blue-100 text-blue-800';
      case '待沟通': return 'bg-gray-100 text-gray-800';
      case '跟进中': return 'bg-blue-100 text-blue-800';
      case '推荐': return 'bg-purple-100 text-purple-800';
      case '面试中': return 'bg-orange-100 text-orange-800';
      case '已获Offer': return 'bg-purple-100 text-purple-800';
      case '已入职': return 'bg-green-100 text-green-800';
      case '淘汰': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTalents = talents;

  const renderCreateModal = () => {
    return (
      <AddTalentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          loadTalents();
        }}
      />
    );
  };

  const renderImportModal = () => {
    if (!showImportModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full mx-4">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">批量导入简历</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card title="外部平台对接">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <i className="ri-briefcase-line text-blue-600 text-xl"></i>
                      <span className="font-medium">智联招聘</span>
                    </div>
                    <Button size="sm">连接</Button>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <i className="ri-user-search-line text-green-600 text-xl"></i>
                      <span className="font-medium">猎聘网</span>
                    </div>
                    <Button size="sm">连接</Button>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <i className="ri-road-map-line text-orange-600 text-xl"></i>
                      <span className="font-medium">前程无忧</span>
                    </div>
                    <Button size="sm">连接</Button>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <i className="ri-chat-3-line text-purple-600 text-xl"></i>
                      <span className="font-medium">BOSS直聘</span>
                    </div>
                    <Button size="sm">连接</Button>
                  </div>
                </div>
              </Card>

              <Card title="文件上传">
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                    <p className="text-gray-600 mb-2">拖拽文件到此处或点击上传</p>
                    <p className="text-sm text-gray-500">支持 PDF、DOC、DOCX 格式</p>
                    <Button className="mt-4" variant="outline">选择文件</Button>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>• 支持批量上传简历文件</p>
                    <p>• AI自动解析简历内容</p>
                    <p>• 自动提取关键信息</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="flex justify-end space-x-4">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>取消</Button>
              <Button>开始导入</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTalentDatabase = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">人才库管理</h1>
          <p className="text-gray-600">新增、编辑、删除和批量导入人才简历</p>
        </div>
        <div className="flex space-x-4">
          <Button variant="outline" onClick={() => setShowDuplicateCheck(true)}>
            <i className="ri-search-2-line mr-2"></i>
            重复检测
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <i className="ri-download-line mr-2"></i>
            批量导入
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <i className="ri-add-line mr-2"></i>
            添加人才
          </Button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{talents.length}</div>
            <div className="text-sm text-gray-600">总人才数</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {talents.filter(t => t.current_status === '离职-立即到岗').length}
            </div>
            <div className="text-sm text-gray-600">可立即到岗</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {talents.filter(t => t.current_status?.includes('面试')).length}
            </div>
            <div className="text-sm text-gray-600">面试进行中</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-1">
              {talents.filter(t => t.years_of_experience && t.years_of_experience >= 5).length}
            </div>
            <div className="text-sm text-gray-600">5年以上经验</div>
          </div>
        </Card>
      </div>

      {/* 筛选条件 */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">状态筛选:</span>
            <div className="flex space-x-2">
              {['all', '待沟通', '跟进中', '推荐', '面试中', '已获Offer', '已入职'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                    filter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? '全部' : status}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Input 
              placeholder="搜索人才..." 
              className="w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* 人才列表 */}
      {loading ? (
        <Card>
          <div className="text-center py-12">
            <i className="ri-loader-4-line text-4xl text-gray-400 mb-4 animate-spin"></i>
            <p className="text-gray-500">加载中...</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTalents.map(talent => (
            <Card key={talent.id} className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <i className="ri-user-fill text-gray-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{talent.name}</h3>
                      <p className="text-gray-600">{talent.position || '未填写职位'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(talent.current_status || '待沟通')}`}>
                    {talent.current_status || '待沟通'}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">经验:</span>
                    <span>{talent.years_of_experience || 0}年</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">地点:</span>
                    <span>{talent.location || '未填写'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">期望薪资:</span>
                    <span className="font-medium text-green-600">{talent.expected_salary || '面议'}</span>
                  </div>
                </div>

                {talent.skills && talent.skills.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-500 mb-2">技能标签:</div>
                    <div className="flex flex-wrap gap-1">
                      {talent.skills.slice(0, 3).map((skill, index) => (
                        <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                          {skill}
                        </span>
                      ))}
                      {talent.skills.length > 3 && (
                        <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs">
                          +{talent.skills.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    更新: {talent.updated_at ? new Date(talent.updated_at).toLocaleDateString() : '未知'}
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline" onClick={() => handleViewDetail(talent.id)}>
                      查看详情
                    </Button>
                    <Button size="sm">联系</Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && filteredTalents.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <i className="ri-user-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">暂无人才</h3>
            <p className="text-gray-500 mb-4">请添加新的人才信息</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <i className="ri-add-line mr-2"></i>
              添加人才
            </Button>
          </div>
        </Card>
      )}
    </div>
  );

  const renderAIResumeAnalysis = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">简历解析(AI) - OCR+NLP结构化</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="AI解析引擎">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-brain-line text-blue-600"></i>
                <span className="font-medium text-blue-800">OCR识别状态</span>
              </div>
              <div className="text-sm text-blue-700">
                今日已处理 <span className="font-bold">87</span> 份简历，准确率 <span className="font-bold">98.5%</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">教育背景提取</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '98%'}}></div>
                  </div>
                  <span className="text-sm font-medium text-green-600">98.5%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">工作经历识别</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '96%'}}></div>
                  </div>
                  <span className="text-sm font-medium text-green-600">96.2%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">技能标签生成</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '94%'}}></div>
                  </div>
                  <span className="text-sm font-medium text-blue-600">94.8%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">期望薪资提取</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{width: '92%'}}></div>
                  </div>
                  <span className="text-sm font-medium text-purple-600">92.3%</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card title="错误高亮与一键修正">
          <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-error-warning-line text-yellow-600"></i>
                <span className="font-medium text-yellow-800">待修正字段</span>
              </div>
              <div className="text-sm text-yellow-700">
                发现 <span className="font-bold">12</span> 个字段需要人工确认
              </div>
            </div>
            
            <div className="space-y-3">
              {[
                { field: '工作年限', original: '三年经验', suggested: '3年', confidence: 85 },
                { field: '期望薪资', original: '20k-25k', suggested: '20K-25K', confidence: 92 },
                { field: '工作城市', original: '深圳', suggested: '深圳市', confidence: 98 }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.field}</span>
                    <span className="text-sm text-blue-600">{item.confidence}%置信度</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">原始:</span>
                      <span className="ml-2 bg-red-100 text-red-800 px-2 py-1 rounded">{item.original}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">建议:</span>
                      <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded">{item.suggested}</span>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-2">
                    <Button size="sm" variant="outline">采纳</Button>
                    <Button size="sm" variant="ghost">忽略</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* NLP结构化字段提取 */}
      <Card title="NLP结构化字段提取">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">AI解析结果预览</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">原始简历文本</h5>
              <div className="bg-white p-3 rounded border text-sm text-gray-600">
                张三，男，28岁，现居北京。北京大学计算机科学与技术专业本科毕业...
                曾在阿里巴巴担任高级前端工程师3年，熟练掌握React、Vue、JavaScript等技术...
                期望薪资25K-35K，可接受上海、深圳工作机会...
              </div>
            </div>
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">结构化提取结果</h5>
              <div className="bg-white p-3 rounded border text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">姓名:</span> 张三</div>
                  <div><span className="text-gray-500">年龄:</span> 28岁</div>
                  <div><span className="text-gray-500">学历:</span> 本科</div>
                  <div><span className="text-gray-500">院校:</span> 北京大学</div>
                  <div><span className="text-gray-500">专业:</span> 计算机科学与技术</div>
                  <div><span className="text-gray-500">工作年限:</span> 3年</div>
                  <div><span className="text-gray-500">当前公司:</span> 阿里巴巴</div>
                  <div><span className="text-gray-500">岗位:</span> 高级前端工程师</div>
                  <div><span className="text-gray-500">期望薪资:</span> 25K-35K</div>
                  <div><span className="text-gray-500">期望城市:</span> 上海,深圳</div>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500">技能标签:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {['React', 'Vue', 'JavaScript', '前端开发'].map((skill, index) => (
                      <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderTagAndProfile = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">标签画像 - 系统标签+自定义标签</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="AI自动打标签">
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-price-tag-3-line text-purple-600"></i>
                <span className="font-medium text-purple-800">智能标签引擎</span>
              </div>
              <div className="text-sm text-purple-700">
                已为 <span className="font-bold">{talents.length}</span> 位人才自动生成标签画像
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">AI标签类型</h4>
              {[
                { category: '偏好类', tags: ['外企偏好', '创业公司偏好', '大厂背景'], count: 45 },
                { category: '能力类', tags: ['技术专家', '团队管理', '跨部门协作'], count: 38 },
                { category: '期望类', tags: ['薪资35-40K', '接受出差', '远程工作'], count: 52 },
                { category: '风险类', tags: ['频繁跳槽', '薪资期望过高', '地域限制'], count: 23 }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.category}</span>
                    <span className="text-sm text-gray-500">{item.count}人</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="自定义标签管理">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">标签库管理</h4>
              <Button size="sm">
                <i className="ri-add-line mr-2"></i>
                新建标签
              </Button>
            </div>
            
            <div className="space-y-3">
              {[
                { name: '高潜力', count: 28, color: 'green', type: '评价标签' },
                { name: '核心候选人', count: 15, color: 'red', type: '重要标签' },
                { name: '技术专家', count: 42, color: 'blue', type: '能力标签' },
                { name: '管理经验', count: 35, color: 'purple', type: '经验标签' },
                { name: '海外背景', count: 18, color: 'orange', type: '背景标签' }
              ].map((tag, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`w-3 h-3 bg-${tag.color}-500 rounded-full`}></span>
                    <div>
                      <div className="font-medium text-gray-900">{tag.name}</div>
                      <div className="text-sm text-gray-500">{tag.type}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{tag.count}人</div>
                    <div className="flex space-x-1 mt-1">
                      <Button size="sm" variant="outline">编辑</Button>
                      <Button size="sm" variant="ghost">删除</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* 标签统计分析 */}
      <Card title="标签统计分析">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">156</div>
            <div className="text-sm text-gray-600">总标签数</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">89</div>
            <div className="text-sm text-gray-600">AI自动标签</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-1">67</div>
            <div className="text-sm text-gray-600">自定义标签</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">95%</div>
            <div className="text-sm text-gray-600">标签覆盖率</div>
          </div>
        </div>
      </Card>

      {/* 人才画像示例 */}
      <Card title="人才画像示例">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-user-fill text-blue-600 text-lg"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">张三 - 高级前端工程师</h4>
                <p className="text-sm text-gray-600">3年经验 · 本科 · 北京</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">AI自动标签</h5>
                <div className="flex flex-wrap gap-1">
                  {['外企偏好', '技术专家', '薪资35-40K', '接受出差', '大厂背景'].map((tag, index) => (
                    <span key={index} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">自定义标签</h5>
                <div className="flex flex-wrap gap-1">
                  {['高潜力', '核心候选人', '技术专家'].map((tag, index) => (
                    <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">标签匹配度分析</h4>
            <div className="space-y-3">
              {[
                { project: '前端开发工程师', match: 95, tags: ['React', '外企偏好', '技术专家'] },
                { project: 'Vue开发工程师', match: 88, tags: ['Vue', '大厂背景', '薪资匹配'] },
                { project: '全栈工程师', match: 72, tags: ['技术专家', '学习能力强'] }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-white rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.project}</span>
                    <span className="text-sm font-medium text-green-600">{item.match}%匹配</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderStatusFlow = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">状态流 - 跨项目多状态并行管理</h2>

      {/* 状态流程图 */}
      <Card title="人才状态流程">
        <div className="space-y-6">
          <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg overflow-x-auto">
            {[
              { status: '待沟通', icon: 'ri-phone-line', color: 'gray', count: talents.filter(t => t.status === '待沟通').length },
              { status: '跟进中', icon: 'ri-chat-3-line', color: 'blue', count: talents.filter(t => t.status === '跟进中').length },
              { status: '推荐', icon: 'ri-send-plane-line', color: 'purple', count: talents.filter(t => t.status === '推荐').length },
              { status: '面试中', icon: 'ri-user-voice-line', color: 'orange', count: talents.filter(t => t.status.includes('面试')).length },
              { status: '已获Offer', icon: 'ri-file-check-line', color: 'green', count: talents.filter(t => t.status === '已获Offer').length },
              { status: '已入职', icon: 'ri-user-star-line', color: 'green', count: talents.filter(t => t.status === '已入职').length }
            ].map((step, index) => (
              <div key={index} className="flex items-center min-w-max">
                <div className={`w-16 h-16 bg-${step.color}-100 rounded-full flex items-center justify-center`}>
                  <i className={`${step.icon} text-${step.color}-600 text-xl`}></i>
                </div>
                <div className="ml-3">
                  <div className="font-medium text-gray-900">{step.status}</div>
                  <div className="text-sm text-gray-500">{step.count} 人</div>
                </div>
                {index < 5 && (
                  <div className="mx-6">
                    <i className="ri-arrow-right-line text-gray-400 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 跨项目状态并行 */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">跨项目多状态并行示例</h4>
            <div className="space-y-3">
              {[
                {
                  name: '张三',
                  projects: [
                    { name: '阿里前端工程师', status: '面试中', stage: '二面' },
                    { name: '腾讯高级前端', status: '推荐', stage: '待安排' },
                    { name: '字节前端专家', status: '跟进中', stage: '意向确认' }
                  ]
                },
                {
                  name: '李四',
                  projects: [
                    { name: '美团Java工程师', status: '已获Offer', stage: '考虑中' },
                    { name: '滴滴后端工程师', status: '面试中', stage: '终面' }
                  ]
                }
              ].map((talent, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-fill text-blue-600"></i>
                    </div>
                    <h5 className="font-medium text-gray-900">{talent.name}</h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {talent.projects.map((project, i) => (
                      <div key={i} className="bg-gray-50 p-3 rounded">
                        <div className="font-medium text-gray-900 text-sm">{project.name}</div>
                        <div className="flex items-center justify-between mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                          <span className="text-xs text-gray-500">{project.stage}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* 状态统计与转化分析 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="状态转化分析">
          <div className="space-y-4">
            <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <i className="ri-line-chart-line text-4xl text-blue-600 mb-3"></i>
                <p className="text-blue-800 font-medium mb-4">转化率统计</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/80 rounded p-3">
                    <div className="text-xl font-bold text-blue-600">75%</div>
                    <div className="text-xs text-blue-700">沟通→推荐</div>
                  </div>
                  <div className="bg-white/80 rounded p-3">
                    <div className="text-xl font-bold text-orange-600">65%</div>
                    <div className="text-xs text-orange-700">推荐→面试</div>
                  </div>
                  <div className="bg-white/80 rounded p-3">
                    <div className="text-xl font-bold text-purple-600">45%</div>
                    <div className="text-xs text-purple-700">面试→Offer</div>
                  </div>
                  <div className="bg-white/80 rounded p-3">
                    <div className="text-xl font-bold text-green-600">85%</div>
                    <div className="text-xs text-green-700">Offer→入职</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card title="状态变更记录">
          <div className="space-y-3">
            {[
              { name: '张三', from: '面试中', to: '已获Offer', time: '2024-01-15 16:30', operator: '李经理', project: '阿里前端工程师' },
              { name: '李四', from: '推荐', to: '面试中', time: '2024-01-15 15:20', operator: '王顾问', project: '腾讯Java工程师' },
              { name: '王五', from: '跟进中', to: '推荐', time: '2024-01-15 14:10', operator: '张总监', project: '字节产品经理' },
              { name: '赵六', from: '已获Offer', to: '已入职', time: '2024-01-15 13:45', operator: '李专员', project: '美团前端' }
            ].map((record, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <i className="ri-user-fill text-gray-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{record.name}</div>
                    <div className="text-sm text-gray-600">{record.project}</div>
                    <div className="text-xs text-gray-500">
                      {record.from} → {record.to}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-900">{record.operator}</div>
                  <div className="text-xs text-gray-500">{record.time}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  const renderCommunicationRecords = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">沟通记录 - AI摘要与版本化管理</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="沟通记录自动归集">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-chat-check-line text-green-600"></i>
                <span className="font-medium text-green-800">自动归集状态</span>
              </div>
              <div className="text-sm text-green-700">
                今日自动归集 <span className="font-bold">45</span> 条沟通记录
              </div>
            </div>
            
            <div className="space-y-3">
              {[
                { type: '电话录音', count: 28, icon: 'ri-phone-line', color: 'blue' },
                { type: '微信聊天', count: 156, icon: 'ri-chat-3-line', color: 'green' },
                { type: '邮件往来', count: 89, icon: 'ri-mail-line', color: 'purple' },
                { type: 'LinkedIn消息', count: 34, icon: 'ri-linkedin-line', color: 'blue' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center space-x-3">
                    <i className={`${item.icon} text-${item.color}-600`}></i>
                    <span className="text-sm text-gray-700">{item.type}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.count}条</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="AI摘要与字段更新">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-robot-line text-blue-600"></i>
                <span className="font-medium text-blue-800">AI处理引擎</span>
              </div>
              <div className="text-sm text-blue-700">
                AI已处理 <span className="font-bold">312</span> 条记录，生成摘要并更新字段
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">AI摘要示例</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">已确认</span>
                </div>
                <div className="text-sm text-gray-700 mb-2">
                  "候选人张三对阿里前端岗位很感兴趣，期望薪资30K，可接受996，但希望有股票期权。面试时间倾向于下周三下午。"
                </div>
                <div className="text-xs text-gray-500">
                  AI识别并更新: 期望薪资→30K, 工作制度偏好→可接受996
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">字段更新建议</span>
                  <div className="flex space-x-1">
                    <Button size="sm" variant="outline">采纳</Button>
                    <Button size="sm" variant="ghost">忽略</Button>
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  基于沟通内容，建议更新期望薪资: 25K-30K → 30K-35K
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 版本化记录 */}
      <Card title="版本化沟通记录">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">张三 - 沟通历史版本</h4>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline">导出记录</Button>
              <Button size="sm">添加记录</Button>
            </div>
          </div>

          <div className="space-y-3">
            {[
              {
                version: 'v1.3',
                time: '2024-01-15 16:30',
                type: '电话沟通',
                operator: '李经理',
                summary: '确认面试安排，候选人对薪资期望有所提升',
                changes: ['期望薪资: 25K-30K → 30K-35K', '面试时间: 待定 → 1月18日下午'],
                status: '已同步'
              },
              {
                version: 'v1.2',
                time: '2024-01-12 14:20',
                type: '微信聊天',
                operator: '王顾问',
                summary: '初步意向确认，发送JD详情',
                changes: ['状态: 待沟通 → 跟进中', '意向度: 未知 → 高'],
                status: '已同步'
              },
              {
                version: 'v1.1',
                time: '2024-01-10 10:15',
                type: '邮件联系',
                operator: '张总监',
                summary: '首次联系，介绍职位机会',
                changes: ['联系状态: 未联系 → 已联系', '反馈: 无 → 感兴趣'],
                status: '已同步'
              }
            ].map((record, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                      {record.version}
                    </span>
                    <div>
                      <span className="font-medium text-gray-900">{record.type}</span>
                      <span className="text-gray-500 ml-2">by {record.operator}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">{record.time}</div>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      {record.status}
                    </span>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg mb-3">
                  <div className="text-sm text-blue-800">{record.summary}</div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">字段变更:</div>
                  <div className="space-y-1">
                    {record.changes.map((change, i) => (
                      <div key={i} className="text-xs text-gray-600">• {change}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );

  const renderPrivacyCompliance = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">隐私与合规 - 数据保护与权限控制</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="联系方式脱敏展示">
          <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-shield-user-line text-yellow-600"></i>
                <span className="font-medium text-yellow-800">脱敏保护</span>
              </div>
              <div className="text-sm text-yellow-700">
                根据用户权限自动脱敏显示敏感信息
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">权限等级示例</h4>
              {[
                { level: '一级权限(总监)', phone: '138****1234', email: 'zhang***@example.com', visible: '部分可见' },
                { level: '二级权限(经理)', phone: '138****1234', email: 'zhan****@***.com', visible: '高度脱敏' },
                { level: '三级权限(顾问)', phone: '138****', email: '****@***.com', visible: '完全脱敏' }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.level}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {item.visible}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>手机: {item.phone}</div>
                    <div>邮箱: {item.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="下载与外发控制">
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-lock-line text-red-600"></i>
                <span className="font-medium text-red-800">访问控制</span>
              </div>
              <div className="text-sm text-red-700">
                严格控制简历下载和外发权限
              </div>
            </div>
            
            <div className="space-y-3">
              {[
                { action: '简历下载', permission: '需要总监审批', icon: 'ri-download-line' },
                { action: '邮件外发', permission: '需要合规确认', icon: 'ri-mail-send-line' },
                { action: '微信转发', permission: '禁止操作', icon: 'ri-share-forward-line' },
                { action: '截图保存', permission: '水印标记', icon: 'ri-screenshot-line' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                  <div className="flex items-center space-x-3">
                    <i className={`${item.icon} text-gray-600`}></i>
                    <span className="text-sm text-gray-700">{item.action}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                    {item.permission}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* 候选人授权记录 */}
      <Card title="候选人授权记录">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">授权状态统计</h4>
            <div className="flex space-x-4">
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">128</div>
                <div className="text-xs text-gray-500">已授权</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-yellow-600">23</div>
                <div className="text-xs text-gray-500">待确认</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-red-600">8</div>
                <div className="text-xs text-gray-500">已拒绝</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            {[
              {
                name: '张三',
                phone: '138****1234',
                authType: '简历推荐授权',
                status: '已授权',
                time: '2024-01-15 14:30',
                validUntil: '2024-07-15',
                projects: ['阿里前端工程师', '腾讯高级前端']
              },
              {
                name: '李四',
                phone: '139****5678',
                authType: '信息使用授权',
                status: '待确认',
                time: '2024-01-14 16:20',
                validUntil: '待确认',
                projects: ['字节Java工程师']
              },
              {
                name: '王五',
                phone: '137****9012',
                authType: '推荐服务授权',
                status: '已拒绝',
                time: '2024-01-13 10:15',
                validUntil: '-',
                projects: []
              }
            ].map((record, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-fill text-blue-600"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{record.name}</div>
                      <div className="text-sm text-gray-500">{record.phone}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    record.status === '已授权' ? 'bg-green-100 text-green-800' :
                    record.status === '待确认' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {record.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">授权类型:</span>
                    <div className="font-medium">{record.authType}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">授权时间:</span>
                    <div className="font-medium">{record.time}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">有效期至:</span>
                    <div className="font-medium">{record.validUntil}</div>
                  </div>
                </div>
                
                {record.projects.length > 0 && (
                  <div className="mt-3">
                    <span className="text-gray-500 text-sm">授权项目:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {record.projects.map((project, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                          {project}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );

  const renderTalentDetail = () => {
    if (!selectedTalent) return null;

    // 从 raw_response 中提取详细信息
    const rawData = selectedTalent.raw_response || {};
    const basicInfo = rawData.basic_info || {};
    const contactInfo = rawData.contact_info || {};
    const workInfo = rawData.work_info || {};
    const expectJobList = rawData.expect_job_list || [];
    const projects = rawData.projects || [];
    const trainings = rawData.trainings || [];
    const practices = rawData.practices || [];
    const langCerts = rawData.lang_certs || [];
    const awards = rawData.awards || [];
    const skills = rawData.skills || [];
    const resumeInfo = rawData.resume_info || {};
    const resumeContent = rawData.resume_content || {};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          {/* 头部 */}
          <div className="sticky top-0 bg-white p-6 border-b border-gray-200 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {selectedTalent.name?.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedTalent.name}</h2>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-gray-600">{selectedTalent.position || selectedTalent.current_position || '未填写职位'}</span>
                    {selectedTalent.gender && (
                      <span className="text-gray-500">· {selectedTalent.gender}</span>
                    )}
                    {selectedTalent.age && (
                      <span className="text-gray-500">· {selectedTalent.age}岁</span>
                    )}
                    {selectedTalent.years_of_experience && (
                      <span className="text-gray-500">· {selectedTalent.years_of_experience}年经验</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedTalent(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* 状态标签 */}
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(selectedTalent.current_status || '待沟通')}`}>
                {selectedTalent.current_status || '待沟通'}
              </span>
              {selectedTalent.job_search_status && (
                <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {selectedTalent.job_search_status}
                </span>
              )}
              {selectedTalent.available_date && (
                <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {selectedTalent.available_date}到岗
                </span>
              )}
            </div>

            {/* 基本信息 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="基本信息">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    {selectedTalent.gender && (
                      <div>
                        <span className="text-sm text-gray-500">性别</span>
                        <div className="font-medium">{selectedTalent.gender}</div>
                      </div>
                    )}
                    {selectedTalent.age && (
                      <div>
                        <span className="text-sm text-gray-500">年龄</span>
                        <div className="font-medium">{selectedTalent.age}岁</div>
                      </div>
                    )}
                    {selectedTalent.birthday && (
                      <div>
                        <span className="text-sm text-gray-500">出生日期</span>
                        <div className="font-medium">{selectedTalent.birthday}</div>
                      </div>
                    )}
                    {basicInfo.ethnic && (
                      <div>
                        <span className="text-sm text-gray-500">民族</span>
                        <div className="font-medium">{basicInfo.ethnic}</div>
                      </div>
                    )}
                    {basicInfo.political_status && (
                      <div>
                        <span className="text-sm text-gray-500">政治面貌</span>
                        <div className="font-medium">{basicInfo.political_status}</div>
                      </div>
                    )}
                    {basicInfo.marital_status && (
                      <div>
                        <span className="text-sm text-gray-500">婚姻状况</span>
                        <div className="font-medium">{basicInfo.marital_status}</div>
                      </div>
                    )}
                    {selectedTalent.location && (
                      <div>
                        <span className="text-sm text-gray-500">所在地</span>
                        <div className="font-medium">{selectedTalent.location}</div>
                      </div>
                    )}
                    {basicInfo.native_place && (
                      <div>
                        <span className="text-sm text-gray-500">籍贯</span>
                        <div className="font-medium">{basicInfo.native_place}</div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <Card title="联系方式">
                <div className="space-y-3">
                  {selectedTalent.phone && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">手机</span>
                      <span className="font-medium">{selectedTalent.phone}</span>
                    </div>
                  )}
                  {selectedTalent.email && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">邮箱</span>
                      <span className="font-medium">{selectedTalent.email}</span>
                    </div>
                  )}
                  {selectedTalent.wechat && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">微信</span>
                      <span className="font-medium">{selectedTalent.wechat}</span>
                    </div>
                  )}
                  {contactInfo.qq && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">QQ</span>
                      <span className="font-medium">{contactInfo.qq}</span>
                    </div>
                  )}
                  {selectedTalent.linkedin_url && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">LinkedIn</span>
                      <a href={selectedTalent.linkedin_url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline cursor-pointer">
                        查看主页
                      </a>
                    </div>
                  )}
                  {selectedTalent.updated_at && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <span className="text-sm text-gray-500">最后更新</span>
                      <span className="font-medium text-sm">
                        {new Date(selectedTalent.updated_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* 工作信息 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="当前工作">
                <div className="space-y-3">
                  {selectedTalent.current_company && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">公司</span>
                      <span className="font-medium">{selectedTalent.current_company}</span>
                    </div>
                  )}
                  {selectedTalent.current_position && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">职位</span>
                      <span className="font-medium">{selectedTalent.current_position}</span>
                    </div>
                  )}
                  {selectedTalent.years_of_experience !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">工作年限</span>
                      <span className="font-medium">{selectedTalent.years_of_experience}年</span>
                    </div>
                  )}
                  {selectedTalent.current_salary && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">当前薪资</span>
                      <span className="font-medium text-green-600">{selectedTalent.current_salary}</span>
                    </div>
                  )}
                  {workInfo.industry && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">所属行业</span>
                      <span className="font-medium">{workInfo.industry}</span>
                    </div>
                  )}
                  {workInfo.start_work_time && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">开始工作时间</span>
                      <span className="font-medium">{workInfo.start_work_time}</span>
                    </div>
                  )}
                </div>
              </Card>

              <Card title="求职意向">
                <div className="space-y-3">
                  {selectedTalent.expected_salary && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">期望薪资</span>
                      <span className="font-medium text-green-600">{selectedTalent.expected_salary}</span>
                    </div>
                  )}
                  {selectedTalent.preferred_position && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">期望职位</span>
                      <span className="font-medium">{selectedTalent.preferred_position}</span>
                    </div>
                  )}
                  {selectedTalent.preferred_industry && selectedTalent.preferred_industry.length > 0 && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-2">期望行业</span>
                      <div className="flex flex-wrap gap-1">
                        {selectedTalent.preferred_industry.map((ind, i) => (
                          <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                            {ind}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedTalent.preferred_location && selectedTalent.preferred_location.length > 0 && (
                    <div>
                      <span className="text-sm text-gray-500 block mb-2">期望地点</span>
                      <div className="flex flex-wrap gap-1">
                        {selectedTalent.preferred_location.map((loc, i) => (
                          <span key={i} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                            {loc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* 教育经历 */}
            {selectedTalent.education && selectedTalent.education.length > 0 && (
              <Card title="教育经历">
                <div className="space-y-4">
                  {selectedTalent.education.map((edu) => (
                    <div key={edu.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{edu.school}</h4>
                            {edu.school_info?.tags && edu.school_info.tags.length > 0 && (
                              <div className="flex gap-1">
                                {edu.school_info.tags.map((tag, i) => (
                                  <span key={i} className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {edu.degree} · {edu.major}
                            {edu.minor && ` · 辅修${edu.minor}`}
                          </div>
                          {edu.dept && (
                            <div className="text-sm text-gray-500">{edu.dept}</div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 text-right">
                          {edu.start_date} - {edu.end_date || '至今'}
                        </div>
                      </div>
                      
                      {(edu.gpa || edu.relative_rank || edu.abs_rank) && (
                        <div className="flex items-center space-x-4 text-sm mt-2">
                          {edu.gpa && (
                            <span className="text-gray-600">GPA: <span className="font-medium">{edu.gpa}</span></span>
                          )}
                          {edu.relative_rank && (
                            <span className="text-gray-600">排名: <span className="font-medium">{edu.relative_rank}</span></span>
                          )}
                          {edu.abs_rank && (
                            <span className="text-gray-600">绝对排名: <span className="font-medium">{edu.abs_rank}</span></span>
                          )}
                        </div>
                      )}
                      
                      {edu.courses && edu.courses.length > 0 && (
                        <div className="mt-2">
                          <span className="text-sm text-gray-500">主修课程: </span>
                          <span className="text-sm text-gray-700">{edu.courses.join('、')}</span>
                        </div>
                      )}
                      
                      {edu.description && (
                        <div className="text-sm text-gray-700 mt-2">{edu.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 工作经历 */}
            {selectedTalent.work && selectedTalent.work.length > 0 && (
              <Card title="工作经历">
                <div className="space-y-4">
                  {selectedTalent.work.map((work) => (
                    <div key={work.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{work.company}</h4>
                          <div className="text-sm text-gray-600">
                            {work.position}
                            {work.dept && ` · ${work.dept}`}
                          </div>
                          {work.company_nature && (
                            <div className="text-sm text-gray-500">{work.company_nature}</div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 text-right">
                          <div>{work.start_date} - {work.end_date || '至今'}</div>
                          {work.duration && (
                            <div className="text-xs text-gray-400">{work.duration}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                        {work.salary && (
                          <div>
                            <span className="text-gray-500">薪资: </span>
                            <span className="font-medium text-green-600">{work.salary}</span>
                          </div>
                        )}
                        {work.location && (
                          <div>
                            <span className="text-gray-500">地点: </span>
                            <span className="font-medium">{work.location}</span>
                          </div>
                        )}
                        {work.industry && (
                          <div>
                            <span className="text-gray-500">行业: </span>
                            <span className="font-medium">{work.industry}</span>
                          </div>
                        )}
                        {work.stuff_size && (
                          <div>
                            <span className="text-gray-500">公司规模: </span>
                            <span className="font-medium">{work.stuff_size}</span>
                          </div>
                        )}
                      </div>
                      
                      {work.description && (
                        <div className="text-sm text-gray-700 mt-3 leading-relaxed">{work.description}</div>
                      )}
                      
                      {work.skills && work.skills.length > 0 && (
                        <div className="mt-3">
                          <span className="text-sm text-gray-500">技能标签: </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {work.skills.map((skill, i) => (
                              <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {work.why_leave && (
                        <div className="text-sm text-gray-600 mt-2">
                          <span className="text-gray-500">离职原因: </span>
                          {work.why_leave}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 项目经历 */}
            {projects && projects.length > 0 && (
              <Card title="项目经历">
                <div className="space-y-4">
                  {projects.map((project: any, index: number) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{project.name}</h4>
                          <div className="text-sm text-gray-600">{project.position}</div>
                          {project.company && (
                            <div className="text-sm text-gray-500">{project.company}</div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {project.start_date} - {project.end_date || '至今'}
                        </div>
                      </div>
                      {project.desc && (
                        <div className="text-sm text-gray-700 mt-2">{project.desc}</div>
                      )}
                      {project.skills && project.skills.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {project.skills.map((skill: string, i: number) => (
                              <span key={i} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 技能与技能 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 专业技能 */}
              {skills && skills.length > 0 && (
                <Card title="专业技能">
                  <div className="space-y-3">
                    {skills.map((skill: any, index: number) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{skill.skill}</span>
                        <div className="flex items-center space-x-2">
                          {skill.level && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              {skill.level}
                            </span>
                          )}
                          {skill.time && (
                            <span className="text-xs text-gray-500">{skill.time}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* 技能 */}
              {selectedTalent.certifications && selectedTalent.certifications.length > 0 && (
                <Card title="技能">
                  <div className="flex flex-wrap gap-2">
                    {selectedTalent.certifications.map((cert, index) => (
                      <span key={index} className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm">
                        {cert}
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* 语言能力 */}
            {langCerts && langCerts.length > 0 && (
              <Card title="语言能力">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {langCerts.map((lang: any, index: number) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{lang.lang}</span>
                        <span className="text-sm text-gray-600">{lang.cert}</span>
                      </div>
                      {lang.score && (
                        <div className="text-sm text-gray-500 mt-1">分数: {lang.score}</div>
                      )}
                      {lang.date && (
                        <div className="text-xs text-gray-400 mt-1">{lang.date}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 获奖经历 */}
            {awards && awards.length > 0 && (
              <Card title="获奖经历">
                <div className="space-y-3">
                  {awards.map((award: any, index: number) => (
                    <div key={index} className="bg-yellow-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{award.name}</span>
                        {award.level && (
                          <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">
                            {award.level}
                          </span>
                        )}
                      </div>
                      {award.date && (
                        <div className="text-sm text-gray-500">{award.date}</div>
                      )}
                      {award.desc && (
                        <div className="text-sm text-gray-700 mt-2">{award.desc}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 培训经历 */}
            {trainings && trainings.length > 0 && (
              <Card title="培训经历">
                <div className="space-y-3">
                  {trainings.map((training: any, index: number) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{training.subject}</div>
                          <div className="text-sm text-gray-600">{training.org}</div>
                          {training.cert && (
                            <div className="text-sm text-green-600 mt-1">技能: {training.cert}</div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {training.start_date} - {training.end_date}
                        </div>
                      </div>
                      {training.desc && (
                        <div className="text-sm text-gray-700 mt-2">{training.desc}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 自我评价 */}
            {selectedTalent.self_evaluation && (
              <Card title="自我评价">
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedTalent.self_evaluation}
                </div>
              </Card>
            )}

            {/* 标签 */}
            {selectedTalent.tags && selectedTalent.tags.length > 0 && (
              <Card title="人才标签">
                <div className="flex flex-wrap gap-2">
                  {selectedTalent.tags.map((tag) => (
                    <span key={tag.id} className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
                      {tag.tag_name}
                    </span>
                  ))}
                </div>
              </Card>
            )}

            {/* 状态历史 */}
            {selectedTalent.statusHistory && selectedTalent.statusHistory.length > 0 && (
              <Card title="状态历史">
                <div className="space-y-2">
                  {selectedTalent.statusHistory.map((history) => (
                    <div key={history.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(history.status)}`}>
                          {history.status}
                        </span>
                        {history.notes && (
                          <span className="ml-2 text-sm text-gray-600">{history.notes}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {history.created_at ? new Date(history.created_at).toLocaleString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* 操作按钮 */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={() => setSelectedTalent(null)}>
                <i className="ri-close-line mr-2"></i>
                关闭
              </Button>
              <Button variant="outline">
                <i className="ri-edit-line mr-2"></i>
                编辑信息
              </Button>
              <Button variant="outline">
                <i className="ri-download-line mr-2"></i>
                下载简历
              </Button>
              <Button variant="outline">
                <i className="ri-refresh-line mr-2"></i>
                状态变更
              </Button>
              <Button>
                <i className="ri-send-plane-line mr-2"></i>
                推荐职位
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* 标签页导航 */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'list', name: '人才库', icon: 'ri-user-line' },
              { id: 'ai-analysis', name: '简历解析', icon: 'ri-brain-line' },
              { id: 'tags', name: '标签画像', icon: 'ri-price-tag-3-line' },
              { id: 'status', name: '状态流', icon: 'ri-flow-chart' },
              { id: 'communication', name: '沟通记录', icon: 'ri-chat-3-line' },
              { id: 'privacy', name: '隐私合规', icon: 'ri-shield-user-line' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 border-b-2 font-medium text-sm cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* 标签页内容 */}
        {activeTab === 'list' && renderTalentDatabase()}
        {activeTab === 'ai-analysis' && <div className="text-center py-12 text-gray-500">简历解析功能开发中...</div>}
        {activeTab === 'tags' && <div className="text-center py-12 text-gray-500">标签画像功能开发中...</div>}
        {activeTab === 'status' && <div className="text-center py-12 text-gray-500">状态流功能开发中...</div>}
        {activeTab === 'communication' && <div className="text-center py-12 text-gray-500">沟通记录功能开发中...</div>}
        {activeTab === 'privacy' && <div className="text-center py-12 text-gray-500">隐私合规功能开发中...</div>}
      </div>

      {/* 模态框 */}
      {renderTalentDetail()}
      {renderCreateModal()}
      {renderImportModal()}
    </div>
  );
}
